import {
    AfterViewInit,
    ChangeDetectionStrategy,
    ChangeDetectorRef,
    Component,
    ElementRef,
    Input,
    OnChanges,
    OnDestroy,
    ViewChild
} from '@angular/core';
import {
    BarController,
    BarElement,
    CategoryScale,
    Chart,
    Legend,
    LinearScale,
    LineController,
    LineElement,
    PointElement,
    Tooltip
} from 'chart.js';
import { MatDialog } from '@angular/material/dialog';
import { MatSnackBar } from '@angular/material/snack-bar';
import { MilkControlSummary, Producer } from '@homeleite/domain';
import {
    ControllerControlRow,
    ControllerEventRow,
    ControllerWork,
    MilkControlsApiRepository
} from '../../../../../../../../libs/domain/src/lib/milk-control/milk-controls-api.repository';
import { PdfGeneratorService } from '../../../../../../../homeleite/src/app/features/milk-controls/milking-control-details/pdf-generator.service';
import {
    loadMilkControlDetail,
    CONTROL_DRAWER_CONFIG, ProducerDetailsMilkControlAnimalsDialogComponent
} from '../../../producers/producer-details/producer-details-milk-controls/producer-details-milk-control-animals-dialog.component';

@Component({
    selector: 'homeleite-controller-work',
    templateUrl: './controller-work.component.html',
    styleUrls: ['./controller-work.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: false
})
export class ControllerWorkComponent implements OnChanges, AfterViewInit, OnDestroy {
    @Input() controller: { id?: string; activeProducers?: any[]; producers?: any[] };
    @ViewChild('evolution', { static: true }) evolutionCanvas: ElementRef<HTMLCanvasElement>;

    readonly monthlyTarget = 45;
    work: ControllerWork = { controls: [], events: [] };
    workLoading = false;
    workError = false;
    pdfId: string | null = null;
    private loadedId: string | null = null;
    private evolutionChart: Chart | null = null;

    constructor(
        private changeDetector: ChangeDetectorRef,
        private snackbar: MatSnackBar,
        private dialog: MatDialog,
        private milkControls: MilkControlsApiRepository,
        private pdf: PdfGeneratorService
    ) {
        Chart.register(
            BarController, BarElement, LineController, LineElement, PointElement,
            CategoryScale, LinearScale, Tooltip, Legend
        );
    }

    ngOnChanges(): void {
        const id = this.controller?.id;
        if (!id || id === this.loadedId) return;
        this.loadedId = id;
        this.loadWork(id);
    }

    ngAfterViewInit(): void {
        this.renderEvolution();
    }

    ngOnDestroy(): void {
        this.evolutionChart?.destroy();
        this.evolutionChart = null;
    }

    private renderEvolution(): void {
        const canvas = this.evolutionCanvas?.nativeElement;
        if (!canvas) return;

        const now = new Date();
        const months = Array.from({ length: 6 }, (_, i) => new Date(now.getFullYear(), now.getMonth() - (5 - i), 1));
        const keyOf = (d: Date) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
        const labels = months.map((d) => d.toLocaleDateString('pt-BR', { month: 'short', year: '2-digit' }));
        const counts = months.map((d) => this.work.controls.filter((c) => (c.day || '').startsWith(keyOf(d))).length);

        this.evolutionChart?.destroy();
        this.evolutionChart = new Chart(canvas, {
            data: {
                labels,
                datasets: [
                    {
                        type: 'bar',
                        label: 'Controles efetuados',
                        data: counts,
                        backgroundColor: '#08734f',
                        borderRadius: 4
                    }
                ]
            },
            options: {
                responsive: true,
                maintainAspectRatio: false,
                plugins: { legend: { position: 'top', labels: { boxWidth: 10, font: { size: 10 } } } },
                scales: {
                    y: { beginAtZero: true, ticks: { precision: 0 } }
                }
            }
        });
    }

    get monthControls(): ControllerControlRow[] {
        const now = new Date();
        const monthKey = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;
        return this.work.controls.filter((c) => (c.day || '').startsWith(monthKey));
    }

    get monthControlCount(): number {
        return this.monthControls.length;
    }

    get monthProducerCount(): number {
        return new Set(this.monthControls.map((c) => c.producerId).filter(Boolean)).size;
    }

    get monthProgress(): number {
        return Math.min(100, Math.round((this.monthControlCount / this.monthlyTarget) * 100));
    }

    syncText(sync: string): string {
        if (sync === 'OK') return 'Enviado';
        if (sync === 'FAIL') return 'Falhou';
        if (sync === 'PENDING') return 'Na fila';
        return 'Sem envio';
    }

    formatDay(day?: string | null): string {
        const parts = (day || '').split('-');
        return parts.length === 3 && parts[2] ? `${parts[2]}/${parts[1]}/${parts[0]}` : '—';
    }

    actionText(event: ControllerEventRow): string {
        const wrote = event.action === 'WriteOperation';
        const read = event.action === 'ReadOperation';
        const verb = wrote ? 'Gravou' : read ? 'Consultou' : 'Registrou';
        return `${verb} ${this.entityText(event.entity)}`;
    }

    producerOf(id: string): string {
        const row = this.work.controls.find((c) => c.producerId === id);
        if (row?.producerCode) return `${row.producerCode} ${row.producerName || ''}`.trim();
        const linked = (this.controller?.activeProducers || this.controller?.producers || [])
            .find((p) => p.id === id);
        return linked ? `${linked.code || ''} ${linked.name || ''}`.trim() : '';
    }

    onOpen(row: ControllerControlRow): void {
        if (!row?.id) return;
        this.dialog.open(ProducerDetailsMilkControlAnimalsDialogComponent, {
            ...CONTROL_DRAWER_CONFIG,
            data: {
                summary: MilkControlSummary.fromControlLike(row),
                producer: new Producer(row.producerId || '', row.producerName || '', row.producerCode || '')
            }
        });
    }

    onPdf(row: ControllerControlRow, event?: Event): void {
        event?.stopPropagation();
        if (!row?.id || this.pdfId) return;
        this.pdfId = row.id;
        this.changeDetector.markForCheck();
        const producer = new Producer(row.producerId || '', row.producerName || '', row.producerCode || '');
        const summary = MilkControlSummary.fromControlLike(row);
        loadMilkControlDetail(this.milkControls, summary, producer).subscribe({
            next: async (loaded) => {
                const hasAnimals = loaded?.milkings?.some((milking) => milking.milkedAnimals?.length);
                this.pdfId = null;
                if (!hasAnimals) {
                    this.snackbar.open('Este controle não tem animais para o PDF.', 'Fechar', { duration: 4000 });
                } else {
                    try {
                        await this.pdf.generateMilkControlReport(loaded);
                    } catch {
                        this.snackbar.open('Não foi possível gerar o PDF.', 'Fechar', { duration: 4000 });
                    }
                }
                this.changeDetector.markForCheck();
            },
            error: () => {
                this.pdfId = null;
                this.snackbar.open('Não foi possível gerar o PDF.', 'Fechar', { duration: 4000 });
                this.changeDetector.markForCheck();
            }
        });
    }

    private entityText(name: string): string {
        const labels: Record<string, string> = {
            MilkControl: 'controle leiteiro',
            MilkedAnimal: 'pesagem',
            Animal: 'animal',
            Producer: 'produtor',
            Controller: 'controlador',
            Pregnancy: 'prenhez',
            Mating: 'cobertura',
            ChildBirth: 'parto',
            Dry: 'secagem',
            HerdOut: 'baixa'
        };
        return labels[name] || (name ? name.toLowerCase() : 'evento');
    }

    private loadWork(controllerId: string): void {
        this.workLoading = true;
        this.workError = false;
        this.milkControls.workByController(controllerId).subscribe({
            next: (work) => {
                this.work = {
                    controls: work?.controls || [],
                    events: work?.events || []
                };
                this.workLoading = false;
                this.renderEvolution();
                this.changeDetector.markForCheck();
            },
            error: () => {
                this.workLoading = false;
                this.workError = true;
                this.changeDetector.markForCheck();
            }
        });
    }
}
